/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { NavigationService } from '@treasury/core/navigation';
import { Feature } from '@treasury/domain/services/feature-flags';
import { TmDetailedError } from '@treasury/domain/shared';
import { UsersService } from '@treasury/domain/users';
import { CreditorsService, WireBeneficiary, WireCreditor } from '@treasury/domain/wires';
import { TmContainer } from '@treasury/presentation';
import '@treasury/presentation/components/tm-actions-column';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/vendor/tm-pagination-controls';
import { TmPaginationControls } from '@treasury/presentation/components/vendor/tm-pagination-controls';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { ContextMenuItem } from '@vaadin/context-menu';
import type { GridSelectedItemsChangedEvent } from '@vaadin/grid';
import { css, html, nothing, PropertyValues } from 'lit';
import { columnBodyRenderer, GridColumnBodyLitRenderer } from 'lit-vaadin-helpers';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { filterTableResults } from '../../../../utilities/table-result-filter';
import '../../../components/templates/treasury-status-column.template';
import './creditors-filter-sheet';
import { initialCreditorFilters } from './creditors-filter-sheet';

export const tagName = 'creditors-table';
@customElement(tagName)
export class CreditorsTable extends TmContainer {
    @property({ attribute: false })
    private creditorDetail: boolean = false;

    @property({ attribute: false })
    private creditors: Array<WireBeneficiary> | Array<WireCreditor> = [];

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private usersService: UsersService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @InjectProperty()
    declare private creditorsService: CreditorsService;

    @state()
    private filteredCreditors: Array<WireBeneficiary> | Array<WireCreditor> = [];

    @state()
    private filteredCreditorCount: number = 0;

    @state()
    private selectedCreditors: Array<WireBeneficiary> | Array<WireCreditor> = [];

    @state()
    private isCreditorList = false;

    @state()
    private filterSheetOpen = false;

    @state()
    private editCreditorFeatureFlag = false;

    @query('tm-pagination-controls')
    private paginationControls!: TmPaginationControls;

    public connectedCallback(): void {
        super.connectedCallback();
        this.loading = true;
    }

    protected async firstUpdated() {
        this.editCreditorFeatureFlag = await (
            await this.featureFlagService
        ).isEnabled(Feature.PwaWireEditCreditor);

        const { route } = await this.navService.getRouteData();
        this.isCreditorList = route.split('/').pop() === 'creditors';
        if (this.isCreditorList) {
            this.dispatchEvent(
                new CustomEvent('applyFilters', {
                    detail: { ...initialCreditorFilters, country: '' },
                })
            );
        } else {
            this.loading = false;
        }
    }

    protected render() {
        const isSelectable = (item: WireCreditor) => {
            if (this.selectedCreditors.length > 0 && this.selectedCreditors[0]?.id === item.id) {
                return true;
            }
            return item.status === 'Ready' && this.selectedCreditors.length < 1;
        };
        return html`<div class="flex flex-col h-full">
            <div class="flex-none">
                <tm-list-header
                    .filterButton=${this.deviceService.isDesktop && this.isCreditorList}
                    @search-term-change=${(e: TmSearchTermChangeEvent) => {
                        this.filterResults(e);
                    }}
                    @filterButtonClick=${() => {
                        this.filterSheetOpen = true;
                    }}
                ></tm-list-header>
            </div>
            ${this.loading
                ? html`<tm-loader card></tm-loader>`
                : html`
                      <div class="flex-grow">
                          <vaadin-grid
                              class=${classMap({
                                  'h-full': true,
                                  'shadow-md': !this.isDesktop,
                              })}
                              .items=${this.filteredCreditors}
                              .isItemSelectable=${isSelectable}
                              @selected-items-changed="${(
                                  e: GridSelectedItemsChangedEvent<WireCreditor | WireBeneficiary>
                              ) => {
                                  this.selectedCreditors = e.detail.value;
                                  this.dispatchEvent(
                                      new CustomEvent('selection-changed', {
                                          detail: this.selectedCreditors,
                                      })
                                  );
                              }}"
                              @active-item-changed=${({ detail }: CustomEvent) => {
                                  if (!detail.value) {
                                      return;
                                  }
                                  if (this.creditorDetail) {
                                      this.navService.navigate(
                                          `/wires/creditors/view/${detail.value.id}`
                                      );
                                  } else {
                                      this.dispatchEvent(
                                          new CustomEvent('select', { detail: detail.value })
                                      );
                                  }
                              }}
                          >
                              ${this.renderColumns()}
                          </vaadin-grid>
                      </div>
                      <div class="flex-none">${this.renderPaginationControls()}</div>
                  `}
            ${this.renderFilterSheet()}
        </div>`;
    }

    protected renderColumns() {
        const workflowColumns = html`<vaadin-grid-sort-column
                header="Creditor Name"
                path="name"
                ${columnBodyRenderer(
                    (item: WireCreditor) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.name}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Account Number"
                path="accountNumber"
                ${columnBodyRenderer(
                    (item: WireCreditor) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.accountNumber}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Agent ID"
                path="bank.bankId"
                ${columnBodyRenderer(
                    (item: WireCreditor) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.bank?.bankId}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Agent Name"
                path="bank.name"
                ${columnBodyRenderer(
                    (item: WireCreditor) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.bank?.name}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Country"
                path="country"
                ${columnBodyRenderer(
                    (item: WireCreditor) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.country}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Currency"
                path="currencyType"
                ${columnBodyRenderer(
                    (item: WireCreditor) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.currencyType}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Created Date"
                path="createdDateFormatted"
                ${columnBodyRenderer(
                    (item: WireCreditor) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${item.createdDateFormatted}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Status"
                path="status"
                ${columnBodyRenderer((creditor: WireCreditor) => {
                    return html`<treasury-status-column
                        .item=${creditor}
                        .completedApproversFunction=${async () =>
                            this.usersService.fetchCompletedApprovers({
                                approvalEntity: 'beneficiary',
                                productId: creditor.id as number | undefined,
                                updatedBy: 0,
                            }) as unknown as string[]}
                        .eligibleApproversFunction=${async () =>
                            this.usersService.fetchAvailableApprovers({
                                approvalEntity: 'beneficiary',
                                productId: creditor.id as number | undefined,
                                updatedBy: 0,
                            }) as unknown as string[]}
                    ></treasury-status-column>`;
                })}
            ></vaadin-grid-sort-column>`;

        if (this.deviceService.isDesktop && !this.isCreditorList) {
            return workflowColumns;
        } else if (this.deviceService.isDesktop && this.isCreditorList) {
            return html`<vaadin-grid-selection-column
                    frozen
                    width="3.5rem"
                ></vaadin-grid-selection-column
                >${workflowColumns}<vaadin-grid-column
                    frozen-to-end
                    flex-grow="0"
                    width="3rem"
                    text-align="end"
                    @click=${(e: Event) => e.stopPropagation()}
                    ${columnBodyRenderer((creditor: WireCreditor) => {
                        const actions: Array<ContextMenuItem> = this.getCreditorActions(creditor);
                        return html`<tm-actions-column
                            .items=${actions}
                            @action-selected=${(e: CustomEvent) => {
                                const action = e.detail.value.text;
                                this.handleActionSelected(action, creditor);
                            }}
                        ></tm-actions-column>`;
                    })}
                ></vaadin-grid-column>`;
        }

        return html`<vaadin-grid-column
            ${columnBodyRenderer(this.renderCreditor as GridColumnBodyLitRenderer<WireCreditor>)}
        ></vaadin-grid-column>`;
    }

    protected renderPaginationControls() {
        if (!this.isDesktop) {
            return nothing;
        }
        return html`<tm-pagination-controls
            .totalItemCount=${this.filteredCreditorCount}
            .itemLabel=${'creditors'}
            @page-changed=${() => {
                this.filterResults();
            }}
        ></tm-pagination-controls>`;
    }

    protected renderFilterSheet() {
        if (!this.isDesktop) {
            return nothing;
        }
        return html`<creditors-filter-sheet
            .open=${this.filterSheetOpen}
            .isCreditorList=${this.isCreditorList}
            @close=${() => {
                this.filterSheetOpen = false;
            }}
            @apply-filters=${(e: CustomEvent) => {
                const filters = e.detail;
                if (!this.isCreditorList) {
                    this.dispatchEvent(
                        new CustomEvent('applyFilters', {
                            detail: {
                                status: 'Ready',
                                beneficiaryType: 'Both',
                                requireEntitlement: false,
                                applicationNameType: 0,
                            },
                        })
                    );
                } else {
                    this.filterSheetOpen = false;
                    this.dispatchEvent(new CustomEvent('applyFilters', { detail: filters }));
                }
            }}
        ></creditors-filter-sheet>`;
    }

    protected updated(_changedProperties: PropertyValues) {
        if (_changedProperties.has('creditors')) {
            this.filteredCreditors = this.creditors;
            this.filteredCreditorCount = this.filteredCreditors.length;
        }
    }

    private filterResults(e?: CustomEvent) {
        let filteredCreditors = this.creditors;
        const fieldsToFilter = this.isDesktop
            ? [
                  'name',
                  'accountNumber',
                  'bank.bankId',
                  'bank.country',
                  'bank.name',
                  'country',
                  'currencyType',
                  'createdDateFormatted',
                  'status',
              ]
            : ['name', 'accountNumber', 'bank.bankId', 'bank.country', 'bank.name'];
        if (e) {
            filteredCreditors = filterTableResults(e, this.creditors, fieldsToFilter);
            this.filteredCreditorCount = filteredCreditors.length;
        }
        if (this.isDesktop) {
            const offset = this.paginationControls?.calculateOffset();
            const limit = this.paginationControls?.pageSize;
            this.filteredCreditors = filteredCreditors.slice(offset, offset + limit);
        } else {
            this.filteredCreditors = filteredCreditors;
        }
    }

    private renderCreditor(item: WireCreditor) {
        return html`<div class="flex justify-between">
            <div class="flex flex-col">
                <div class="text-sm font-medium">${item.name}</div>
                <div class="text-xs text-secondary">${item.accountNumber}</div>
            </div>
            <div class="flex flex-col">
                <div class="flex justify-end">
                    <div class="text-sm font-medium">${item.bank?.bankId}</div>
                    <div class="text-xs ml-1 self-center">${item.bank?.country}</div>
                </div>
                <div class="self-end text-xs text-secondary">${item.bank?.name}</div>
            </div>
        </div>`;
    }

    private getCreditorActions(creditor: WireCreditor): Array<ContextMenuItem> {
        const actions: Array<ContextMenuItem> = [];
        const permissions = creditor.permissions.map(p => p.permission);

        if (
            permissions.includes('Edit') &&
            creditor.status !== 'Pending Approval' &&
            this.editCreditorFeatureFlag
        ) {
            actions.push({
                text: 'Edit',
            });
        }
        if (permissions.includes('Delete')) {
            actions.push({
                text: 'Delete',
            });
        }
        if (permissions.includes('CreateWire') && creditor.status === 'Ready') {
            actions.push({ text: 'Initiate Wire' });
        }
        if (permissions.includes('Approve')) {
            actions.push({ text: 'Approve' });
        }
        if (permissions.includes('Reject')) {
            actions.push({ text: 'Reject' });
        }
        return actions;
    }

    private handleActionSelected(action: string, creditor: WireCreditor) {
        switch (action) {
            case 'Edit':
                this.navService.navigate(`/wires/creditors/edit/${creditor.id}`);
                break;
            case 'Delete':
                this.deleteCreditor(creditor);
                break;
            case 'Initiate Wire':
                this.navService.navigate(
                    `/wires/${creditor.isInternational ? 'international' : 'domestic'}/create/${creditor.id}/usd`
                );
                break;
            case 'Approve':
                this.approveCreditor(creditor);
                break;
            case 'Reject':
                this.rejectCreditor(creditor);
                break;
            default:
                break;
        }
    }

    private async approveCreditor(creditor: WireCreditor) {
        const { reason } = await this.dialogService.open(
            `Are you sure you want to approve this creditor?`,
            'Confirm Approval',
            {
                buttons: {
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Approve',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                },
                classes: ['max-w-[500px]'],
            }
        );
        if (reason === DynamicComponentExitReason.Confirm) {
            this.loading = true;
            try {
                await this.creditorsService.approve(creditor.id as number, '');
                //    dispatch correct event to refetch creditors list
            } catch (error) {
                if (error instanceof TmDetailedError) {
                    this.notificationService.renderError(error);
                } else if (error instanceof Error) {
                    this.notificationService.renderError(error);
                } else {
                    this.notificationService.renderError('Error processing your wire');
                }
            } finally {
                this.loading = false;
            }
        }
        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
    }

    private async rejectCreditor(creditor: WireCreditor) {
        const { reason } = await this.dialogService.open(
            `Are you sure you want to reject this creditor?`,
            'Confirm Rejection',
            {
                buttons: {
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Reject',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                },
                classes: ['max-w-[500px]'],
            }
        );
        if (reason === DynamicComponentExitReason.Confirm) {
            this.loading = true;
            try {
                await this.creditorsService.reject(creditor.id, '');
                //    dispatch correct event to refetch creditors list
            } catch (error) {
                if (error instanceof TmDetailedError) {
                    this.notificationService.renderError(error);
                } else if (error instanceof Error) {
                    this.notificationService.renderError(error);
                } else {
                    this.notificationService.renderError('Error processing your wire');
                }
            } finally {
                this.loading = false;
            }
        }
        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
    }

    private async deleteCreditor(creditor?: WireCreditor) {
        const { reason } = await this.dialogService.open(
            `Are you sure you want to delete this Creditor?`,
            'Confirm Deletion',
            {
                buttons: {
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Delete',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                },
                classes: ['max-w-[500px]'],
            }
        );
        if (reason === DynamicComponentExitReason.Confirm) {
            this.loading = true;
            try {
                await this.creditorsService.delete(creditor?.id as number);
                this.navService.navigate('/wires/creditors');
            } catch (error) {
                if (error instanceof TmDetailedError) {
                    this.notificationService.renderError(error);
                } else if (error instanceof Error) {
                    this.notificationService.renderError(error);
                } else {
                    this.notificationService.renderError('Error processing your wire');
                }
            } finally {
                this.loading = false;
            }
        }
        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
    }

    public static get styles() {
        return [
            css`
                vaadin-grid::part(header-cell) {
                    min-height: auto;
                    padding-top: 3px;
                    background-color: var(--border-color);
                    font-size: 0.75rem;
                    line-height: calc(1 / 0.75);
                }
                vaadin-grid-sorter::part(indicators) {
                    opacity: 0.5;
                }
                vaadin-grid-sorter[direction]::part(indicators) {
                    opacity: 100;
                }
            `,
        ];
    }
}
declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CreditorsTable;
    }
}
